import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

import { scrubAuthFromUrl } from './utils/scrubAuthFromUrl';
import { useDarkMode } from './hooks/useDarkMode';
import ProjectList from './views/ProjectList';
import Annotator from './views/Annotator';

const DEFAULT_ORG_ID = import.meta.env.VITE_DEFAULT_ORG_ID;
const IGNODE_MODE = import.meta.env.VITE_IGNODE_MODE === 'true';
const PORTAL_URL = import.meta.env.VITE_IGNODE_PORTAL_URL ?? '';

/**
 * App — router + cross-cutting providers (theme, toaster, i18n).
 *
 * Mode handling per UI mockups § "Mode detection":
 *   - Standalone (VITE_IGNODE_MODE=false): /org/:orgId shows the project
 *     list inside the SPA.
 *   - IGNODE mode (VITE_IGNODE_MODE=true): the upstream proxy 302s
 *     /org/:orgId to the IGNODE Portal's Label Factory page; the SPA
 *     entry is /org/:orgId/project/:projectId. If a user lands on /
 *     directly we bounce them out of the SPA to the portal (similar to
 *     agent-builder's behavior in App.jsx).
 */
export default function App() {
    // Initialize the theme system — the hook applies <html class="dark"> on
    // mount, listens for system pref changes, and hydrates the user's
    // server-stored theme on first load.
    useDarkMode();

    useEffect(() => {
        scrubAuthFromUrl();
    }, []);

    return (
        <BrowserRouter>
            <Toaster
                position="bottom-right"
                toastOptions={{
                    className: '!bg-gray-800 !text-gray-100 !rounded-lg !shadow-lg',
                }}
            />
            <Routes>
                {/* Root → either redirect to portal (IGNODE) or to default org's project list (standalone) */}
                <Route path="/" element={<RootBounce />} />

                {/* Standalone mode entry — in IGNODE mode this redirects
                    to the Portal's Label Factory page (per D.7: the
                    Portal owns project management; the annotator is
                    a child window that opens directly to /project/:id). */}
                <Route path="/org/:orgId" element={
                    IGNODE_MODE ? <RootBounce /> : <ProjectList />
                } />

                {/* The annotation canvas */}
                <Route path="/org/:orgId/project/:projectId" element={<Annotator />} />

                {/* Fallback — bounce to default org list (standalone) or portal (IGNODE) */}
                <Route path="*" element={<RootBounce />} />
            </Routes>
        </BrowserRouter>
    );
}

function RootBounce() {
    if (IGNODE_MODE) {
        // First try closing the tab — if Portal opened us via window.open
        // this returns us to the Portal tab. Otherwise (user typed the
        // URL directly), redirect to the Portal's Label Factory page.
        // Without PORTAL_URL we still try close() so at least the tab
        // doesn't sit on a Navigate-loop blank screen.
        window.close();
        window.setTimeout(() => {
            if (PORTAL_URL) {
                window.location.href = `${PORTAL_URL}/dashboard/integrations/label-factory`;
            }
        }, 0);
        return null;
    }
    return <Navigate to={`/org/${DEFAULT_ORG_ID}`} replace />;
}
